import { WarningIcon } from '../../../assets/icons/warning-icon.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { api } from '../../../utils/api.ts';
import { BaseConfirmationDialog } from '../../dialog/confirmation-dialog.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import type { DialogInnerProps } from '../../dialog/types.ts';

type NoteDeleteDialogProps = {
    noteId: string;
    onDeleted?: () => void;
} & DialogInnerProps;

export const NoteDeleteDialog = DialogStateManager.create<NoteDeleteDialogProps>(({ noteId, onDeleted }) => {
    const { remove } = useDialog();
    const { t } = useLocale();
    const { toast } = useToast();
    const apiUtils = api.useUtils();

    const deleteMutation = api.platoUser.command.notes.delete.useMutation({
        onSuccess: () => {
            remove();

            toast({
                icon: 'success',
                title: t('notes__delete_confirmation__toast__success_title', { ns: 'notes' }),
                description: t('notes__delete_confirmation__toast__success_description', {
                    ns: 'notes',
                }),
            });

            void Promise.all([
                apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate(),
                apiUtils.platoUser.query.customerActivity.getFeedList.getPinnedNotes.invalidate(),
            ]);

            onDeleted?.();
        },
        onError: (error) => {
            toast({
                icon: 'warning',
                title: 'Error',
                description: error.message,
            });
        },
    });

    const handleDelete = () => {
        deleteMutation.mutate({ noteId });
    };

    return (
        <BaseConfirmationDialog
            focus="destroy"
            icon={WarningIcon}
            title={t('notes__delete_confirmation__title', { ns: 'notes' })}
            description={t('notes__delete_confirmation__description', { ns: 'notes' })}
        >
            <BaseConfirmationDialog.Actions>
                <BaseConfirmationDialog.RejectButton onClick={remove}>
                    {t('form__cancel')}
                </BaseConfirmationDialog.RejectButton>
                <BaseConfirmationDialog.AcceptButton
                    onClick={handleDelete}
                    isLoading={deleteMutation.isLoading}
                >
                    {t('form__delete')}
                </BaseConfirmationDialog.AcceptButton>
            </BaseConfirmationDialog.Actions>
        </BaseConfirmationDialog>
    );
});
